import { useState, useEffect, useCallback } from 'react';

interface TextStats {
  wordCount: number;
  charCount: number;
  charNoSpacesCount: number;
  wordsPerMinute: number;
  deletedChars: number;
  deletedWords: number;
}

export function useTextStats(text: string) {
  const [stats, setStats] = useState<TextStats>({
    wordCount: 0,
    charCount: 0,
    charNoSpacesCount: 0,
    wordsPerMinute: 0,
    deletedChars: 0,
    deletedWords: 0
  });
  
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [lastSavedLength, setLastSavedLength] = useState<number>(0);
  const [lastText, setLastText] = useState<string>('');
  const [totalCharsTyped, setTotalCharsTyped] = useState<number>(0);
  const [totalWordsTyped, setTotalWordsTyped] = useState<number>(0);
  
  // Calculate text statistics
  const calculateStats = useCallback((text: string): TextStats => {
    const trimmedText = text.trim();
    const wordCount = trimmedText ? trimmedText.split(/\s+/).length : 0;
    const charCount = text.length;
    const charNoSpacesCount = text.replace(/\s+/g, '').length;
    
    // Calculate words per minute (basic calculation)
    const elapsedMinutes = (Date.now() - startTime) / 60000;
    const wordsPerMinute = elapsedMinutes > 0 
      ? Math.round(wordCount / elapsedMinutes) 
      : 0;
    
    // Track total characters typed (for statistics)
    if (lastText.length > 0 && text.length > lastText.length) {
      const charsAdded = text.length - lastText.length;
      setTotalCharsTyped(prev => prev + charsAdded);
      
      // Estimate words typed
      const wordsAdded = Math.max(0, Math.floor(charsAdded / 5));
      setTotalWordsTyped(prev => prev + wordsAdded);
    }
    
    return {
      wordCount,
      charCount,
      charNoSpacesCount,
      wordsPerMinute,
      deletedChars: stats.deletedChars, // Only from inactivity deletions
      deletedWords: stats.deletedWords   // Only from inactivity deletions
    };
  }, [startTime, lastText, stats.deletedChars, stats.deletedWords]);
  
  // Update statistics
  const updateStats = useCallback(() => {
    const newStats = calculateStats(text);
    setStats(newStats);
    setLastText(text);
  }, [calculateStats, text]);
  
  // Initialize stats when text changes
  useEffect(() => {
    // Reset start time when starting from empty
    if (text.length === 1 && lastSavedLength === 0) {
      setStartTime(Date.now());
      setTotalCharsTyped(0);
      setTotalWordsTyped(0);
    }
    
    updateStats();
    setLastSavedLength(text.length);
  }, [text, lastSavedLength, updateStats]);

  // Reset function for new sessions
  const resetStats = useCallback(() => {
    setStartTime(Date.now());
    setLastText('');
    setTotalCharsTyped(0);
    setTotalWordsTyped(0);
    setStats({
      wordCount: 0,
      charCount: 0,
      charNoSpacesCount: 0,
      wordsPerMinute: 0,
      deletedChars: 0,
      deletedWords: 0
    });
  }, []);
  
  // Function to manually add inactivity deletions (called from App when inactivity timer fires)
  const addInactivityDeletion = useCallback((deletedText: string) => {
    const deletedCharCount = deletedText.length;
    const deletedWordCount = deletedText.trim() ? deletedText.trim().split(/\s+/).length : 0;
    
    setStats(prev => ({
      ...prev,
      deletedChars: prev.deletedChars + deletedCharCount,
      deletedWords: prev.deletedWords + deletedWordCount
    }));
  }, []);

  return {
    ...stats,
    updateStats,
    resetStats,
    addInactivityDeletion,
    totalCharsTyped,
    totalWordsTyped
  };
} 